import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const useSalesData = ({ currentLocationId }) => {
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [receiptsLimit, setReceiptsLimit] = useState(25);
  const [receiptsOffset, setReceiptsOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDate, setFilterDate] = useState('all');
  const [filterDateValue, setFilterDateValue] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // KSeF state
  const [ksefEnabled, setKsefEnabled] = useState(false);
  const [ksefSending, setKsefSending] = useState({});

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptEligibility, setReceiptEligibility] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedInvoiceForCorrection, setSelectedInvoiceForCorrection] = useState(null);

  // --- Data fetching ---

  const fetchInvoices = useCallback(async () => {
    try {
      const params = currentLocationId ? `?location_id=${currentLocationId}` : '';
      const response = await api.get(`/sales-invoices${params}`);
      const data = response.data;

      if (data.success) {
        setInvoices(data.data.faktury || []);
      } else {
        setError('Blad pobierania faktur: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, [currentLocationId]);

  const fetchReceipts = useCallback(async () => {
    try {
      let url = `/receipts-for-invoicing?limit=${receiptsLimit}&offset=${receiptsOffset}`;

      if (currentLocationId) {
        url += `&location_id=${currentLocationId}`;
      }

      if (filterDate === 'today') {
        const today = new Date().toISOString().split('T')[0];
        url += `&date=${today}`;
      } else if (filterDate === 'month') {
        const currentMonth = new Date().toISOString().substring(0, 7);
        url += `&month=${currentMonth}`;
      } else if (filterDate === 'custom' && filterDateValue) {
        url += `&date=${filterDateValue}`;
      }

      const response = await api.get(url);
      const data = response.data;

      if (data.success) {
        setReceipts(data.data.paragony || []);
        setTotalReceipts(data.data.total || 0);
      } else {
        setError('Blad pobierania paragonow: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, [currentLocationId, receiptsLimit, receiptsOffset, filterDate, filterDateValue]);

  const fetchCustomers = useCallback(async () => {
    try {
      // rola=odbiorca: kartoteka jest wspolna dla klientow i dostawcow (D1),
      // wiec bez tego filtra na liscie nabywcow faktury sprzedazy pojawiliby sie
      // dostawcy (SFD i spolka) - i faktura wyszlaby na dostawce.
      const response = await api.get('/customers', {
        params: { rola: 'odbiorca', page: 1, per_page: 200 }
      });
      const data = response.data;

      if (data.success) {
        // Backend zwraca {items,total,page,per_page} gdy podano page/per_page,
        // a plaska liste w trybie zgodnosci - obslugujemy oba ksztalty.
        const body = data.data;
        setCustomers(Array.isArray(body) ? body : (body?.items || []));
      } else {
        setError('Blad pobierania klientow: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, []);

  const fetchKsefSettings = useCallback(async () => {
    try {
      const response = await api.get('/ksef/settings');
      const data = response.data;
      if (data?.success) {
        setKsefEnabled(Boolean(data.data?.enabled));
      }
    } catch (err) {
      // KSeF settings not available — ignore (np. brak modulu lub brak uprawnien)
    }
  }, []);

  // --- KSeF actions ---

  const sendToKsef = useCallback(async (invoiceId) => {
    if (ksefSending[invoiceId]) return;

    setKsefSending(prev => ({ ...prev, [invoiceId]: true }));

    try {
      // axios + interceptor w api.js: status 2xx -> response.data; inne -> throw Error z .status i .message
      const response = await api.post(`/ksef/send/${invoiceId}`, null, { timeout: 120000 });
      const data = response.data || {};

      if (data.success) {
        toast.success(data.message || 'Faktura zostala wyslana do KSeF');
        fetchInvoices();
      } else {
        // Backend zwrocil 2xx ale success=false (rzadkie, ale obsluzmy)
        const msg = data.message || data.error || 'Nieznany blad';
        if (msg.toLowerCase().includes('juz') && msg.toLowerCase().includes('zaakceptowan')) {
          toast(msg, { icon: 'ℹ️', duration: 6000 });
          fetchInvoices();
        } else {
          toast.error('Blad wysylania do KSeF: ' + msg);
        }
      }
    } catch (err) {
      // Uwaga: api.js interceptor przepakowuje axios error w plain Error z .status i .message.
      // err.response/err.code NIE sa dostepne tutaj.
      console.error('[KSeF] Send error:', err);
      const status = err?.status;
      const msg = err?.message || 'Nieznany blad';

      // 401/403 — auth/permissions. Interceptor sam pokazuje toast lub redirect, tylko log.
      if (err?.isAuthError || err?.isPermissionError) {
        return;
      }

      // 5xx i NETWORK — interceptor JUZ pokazal toast. Nie duplikujemy.
      if (status >= 500 || msg.toLowerCase().includes('brak polaczenia')) {
        return;
      }

      // Timeout (axios ECONNABORTED bywa rzucany przed interceptorem error.request branch)
      if (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('brak polaczenia z serwerem')) {
        toast.error('Timeout - serwer nie odpowiedzial w ciagu 2 minut. Autoryzacja KSeF moze trwac dluzej.');
        return;
      }

      // 409 lub message mowi ze faktura juz zaakceptowana - to INFO, nie blad
      const looksLikeDuplicate = status === 409
        || (msg.toLowerCase().includes('juz') && msg.toLowerCase().includes('zaakceptowan'));
      if (looksLikeDuplicate) {
        toast(msg || 'Faktura juz wczesniej zostala zaakceptowana w KSeF', { icon: 'ℹ️', duration: 6000 });
        fetchInvoices();  // odswiez liste zeby zaktualizowac status
        return;
      }

      // 400/422 - blad walidacji
      if (status === 400 || status === 422) {
        toast.error('Blad walidacji: ' + msg, { duration: 7000 });
        return;
      }

      // Default
      toast.error('Blad wysylania do KSeF: ' + msg);
    } finally {
      setKsefSending(prev => ({ ...prev, [invoiceId]: false }));
    }
  }, [ksefSending, fetchInvoices]);

  const downloadUpo = useCallback(async (invoiceId, ksefNumer) => {
    try {
      const response = await api.get(`/ksef/upo/${invoiceId}`, { timeout: 60000 });
      const data = response.data;

      if (data.success && data.data?.upo) {
        const blob = new Blob([data.data.upo], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `UPO_${ksefNumer || invoiceId}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('UPO pobrane');
      } else {
        toast.error('Nie udalo sie pobrac UPO: ' + (data.error || data.message || 'Brak danych UPO'));
      }
    } catch (err) {
      // api.js interceptor pokazal toast dla 5xx/network — nie duplikuj
      console.error('Blad pobierania UPO:', err);
      if (err?.isAuthError || err?.isPermissionError) return;
      const status = err?.status;
      const msg = err?.message || 'Nieznany blad';
      if (status >= 500 || msg.toLowerCase().includes('brak polaczenia')) return;
      toast.error('Blad pobierania UPO: ' + msg);
    }
  }, []);

  // --- Invoice actions ---

  const previewInvoice = useCallback(async (invoiceId) => {
    try {
      const response = await api.get(`/sales-invoices/${invoiceId}`);
      const data = response.data;

      if (data.success) {
        toast.success(`Podglad faktury ${data.data.numer_faktury} - Suma: ${data.data.suma_brutto} zl`);
      } else {
        setError('Blad pobierania faktury: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, []);

  const openCorrectionModal = useCallback((invoice) => {
    setSelectedInvoiceForCorrection(invoice);
    setShowCorrectionModal(true);
  }, []);

  const handleCorrectionComplete = useCallback(() => {
    setShowCorrectionModal(false);
    setSelectedInvoiceForCorrection(null);
    fetchInvoices();
  }, [fetchInvoices]);

  // --- Receipt actions ---

  const checkReceiptEligibility = useCallback(async (receiptId) => {
    try {
      const response = await api.get(`/receipt-invoice-eligibility/${receiptId}`);
      const data = response.data;

      if (data.success) {
        setReceiptEligibility(data.data);
      }
    } catch (err) {
      console.error('Blad sprawdzania uprawnien:', err);
    }
  }, []);

  const fetchReceiptDetails = useCallback(async (receiptId, canCreateInvoices) => {
    if (!canCreateInvoices) {
      setError('Brak uprawnien do wystawiania faktur');
      return;
    }

    try {
      const response = await api.get(`/receipt-details/${receiptId}`);
      const data = response.data;

      if (data.success) {
        setSelectedReceipt(data.data);
        await checkReceiptEligibility(receiptId);
        setShowCreateModal(true);
      } else {
        setError('Blad pobierania szczegolow paragonu: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, [checkReceiptEligibility]);

  const createInvoiceFromReceipt = useCallback(async (receiptId, invoiceData) => {
    try {
      const response = await api.post(`/sales-invoices/from-receipt/${receiptId}`, invoiceData);

      const data = response.data;

      if (data.success) {
        toast.success('Faktura utworzona pomyslnie!');
        setShowCreateModal(false);
        setSelectedReceipt(null);
        setReceiptEligibility(null);
        setSelectedCustomer(null);
        fetchInvoices();
        fetchReceipts();
      } else {
        setError('Blad tworzenia faktury: ' + data.error);
      }
    } catch (err) {
      setError('Blad polaczenia z serwerem: ' + err.message);
    }
  }, [fetchInvoices, fetchReceipts]);

  // --- Pagination helpers ---

  const goToPage = useCallback((page) => {
    const newOffset = (page - 1) * receiptsLimit;
    setCurrentPage(page);
    setReceiptsOffset(newOffset);
  }, [receiptsLimit]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilterDate(newFilter);
    setCurrentPage(1);
    setReceiptsOffset(0);
  }, []);

  const handleReceiptsLimitChange = useCallback((newLimit) => {
    setReceiptsLimit(newLimit);
    setReceiptsOffset(0);
    setCurrentPage(1);
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setSelectedReceipt(null);
    setReceiptEligibility(null);
    setSelectedCustomer(null);
  }, []);

  const closeCorrectionModal = useCallback(() => {
    setShowCorrectionModal(false);
    setSelectedInvoiceForCorrection(null);
  }, []);

  // --- Initial load ---

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchInvoices(), fetchReceipts(), fetchCustomers(), fetchKsefSettings()])
      .finally(() => setLoading(false));
  }, [receiptsLimit, receiptsOffset, filterDate, filterDateValue, currentLocationId]);

  return {
    // Data
    invoices,
    receipts,
    totalReceipts,
    customers,
    loading,
    error,
    setError,

    // KSeF
    ksefEnabled,
    ksefSending,
    sendToKsef,
    downloadUpo,

    // Invoice actions
    fetchInvoices,
    previewInvoice,
    openCorrectionModal,
    handleCorrectionComplete,
    showCorrectionModal,
    selectedInvoiceForCorrection,
    closeCorrectionModal,

    // Receipt state & actions
    receiptsLimit,
    currentPage,
    filterDate,
    filterDateValue,
    setFilterDateValue,
    handleFilterChange,
    handleReceiptsLimitChange,
    goToPage,
    fetchReceiptDetails,
    createInvoiceFromReceipt,

    // Modal state
    showCreateModal,
    selectedReceipt,
    receiptEligibility,
    closeCreateModal,
  };
};

export default useSalesData;
